import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MapPin, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const PLACE_TYPES = [
  { value: 'Hill Station', label: 'Hill Station' },
  { value: 'Beach', label: 'Beach' },
  { value: 'City', label: 'City' },
  { value: 'Wildlife', label: 'Wildlife' },
  { value: 'Heritage', label: 'Heritage' },
  { value: 'Pilgrimage', label: 'Pilgrimage' },
];

const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry'
];

const QuickAddPlaceModal = ({ 
  open, 
  onOpenChange, 
  prefillName = '', 
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    place_name: prefillName,
    state: '',
    country: 'India',
    place_type: 'City',
  });

  // Reset form when modal opens with new prefill
  React.useEffect(() => {
    if (open) {
      setForm(prev => ({
        ...prev,
        place_name: prefillName,
      }));
    }
  }, [open, prefillName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.place_name.trim()) {
      toast.error('Place name is required');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/places', {
        place_name: form.place_name.trim(),
        state: form.state || null,
        country: form.country || 'India',
        place_type: form.place_type || 'City',
        is_active: true,
      });

      toast.success(`Place "${form.place_name}" added successfully`);
      
      // Call success callback with the new place data
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      // Reset form and close
      setForm({
        place_name: '',
        state: '',
        country: 'India',
        place_type: 'City',
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create place:', error);
      const message = error.response?.data?.detail || 'Failed to add place';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quick-add-place-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Quick Add Place
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="place_name">Place Name <span className="text-red-500">*</span></Label>
            <Input
              id="place_name"
              value={form.place_name}
              onChange={(e) => setForm({ ...form, place_name: e.target.value })}
              placeholder="e.g., Wayanad"
              data-testid="quick-add-place-name"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="state">State</Label>
            <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
              <SelectTrigger data-testid="quick-add-place-state">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {STATES.map(state => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <Input
              id="country"
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
              placeholder="India"
              data-testid="quick-add-place-country"
            />
          </div>

          <div className="space-y-2">
            <Label>Place Type</Label>
            <div className="grid grid-cols-3 gap-2">
              {PLACE_TYPES.map(type => (
                <Button
                  key={type.value}
                  type="button"
                  variant={form.place_type === type.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setForm({ ...form, place_type: type.value })}
                  className="text-xs"
                  data-testid={`quick-add-place-type-${type.value.toLowerCase().replace(' ', '-')}`}
                >
                  {type.label}
                </Button>
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="quick-add-place-submit">
              <Plus className="w-4 h-4 mr-1" />
              {loading ? 'Adding...' : 'Add & Select'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddPlaceModal;
